import {Product} from "../services/receipt/receiptModel";

/**
 * Money helpers that operate in integer cents to avoid floating point drift.
 * All split logic funnels through here so that the sum of every member's share
 * is always exactly equal to the amount that was distributed - no rounding
 * money is ever created or lost.
 */

export function toCents(amount: number): number {
  return Math.round((amount + Number.EPSILON) * 100);
}

export function fromCents(cents: number): number {
  return cents / 100;
}

/**
 * The line total for a product is the UNIT price multiplied by the (possibly
 * fractional) quantity, rounded once to the nearest cent.
 */
export function lineTotalCents(product: Pick<Product, 'price' | 'quantity'>): number {
  const price = Number.isFinite(product.price) ? product.price : 0;
  const quantity = Number.isFinite(product.quantity) ? product.quantity : 0;
  return Math.round((price * quantity + Number.EPSILON) * 100);
}

/**
 * Split an integer amount of cents evenly across `count` buckets. The remainder
 * cents (there are at most `count - 1` of them) are handed out one-by-one so the
 * returned array always sums back to `totalCents`. `startIndex` rotates which
 * bucket receives the first remainder cent, so callers that split many rows can
 * spread the leftover pennies fairly instead of always favouring bucket 0.
 */
export function splitCentsEvenly(totalCents: number, count: number, startIndex = 0): number[] {
  if (count <= 0) {
    return [];
  }
  const sign = totalCents < 0 ? -1 : 1;
  const abs = Math.abs(totalCents);
  const base = Math.floor(abs / count);
  const remainder = abs - base * count;
  const offset = ((startIndex % count) + count) % count;
  const result: number[] = [];
  for (let i = 0; i < count; i++) {
    // Bucket i gets an extra cent when its rotated position falls within the
    // first `remainder` slots.
    const rotated = (i - offset + count) % count;
    const share = base + (rotated < remainder ? 1 : 0);
    result.push(share * sign);
  }
  return result;
}

export interface AssignableItem {
  id: string | number;
  lineTotalCents: number;
  /** Member ids this item is assigned to. Empty means unassigned. */
  memberIds: (string | number)[];
}

export interface AllocationResult {
  /** Member id -> owed cents. */
  perMember: Record<string, number>;
  /** Items that have no member assigned yet (reported, never silently dropped). */
  unassigned: AssignableItem[];
  /** Sum of everything that was actually allocated, in cents. */
  allocatedCents: number;
}

function emptyPerMember(memberIds: (string | number)[]): Record<string, number> {
  return memberIds.reduce<Record<string, number>>((acc, id) => {
    acc[String(id)] = 0;
    return acc;
  }, {});
}

/**
 * Split a single total evenly across every member.
 */
export function allocateEqually(
  totalCents: number,
  memberIds: (string | number)[]
): AllocationResult {
  const perMember = emptyPerMember(memberIds);
  if (memberIds.length === 0) {
    return {perMember, unassigned: [], allocatedCents: 0};
  }
  const shares = splitCentsEvenly(totalCents, memberIds.length);
  memberIds.forEach((id, index) => {
    perMember[String(id)] = shares[index];
  });
  return {perMember, unassigned: [], allocatedCents: totalCents};
}

/**
 * Allocate each item's line total across the members it was assigned to. Items
 * assigned to nobody are surfaced in `unassigned` rather than being dropped.
 */
export function allocateByAssignment(
  items: AssignableItem[],
  memberIds: (string | number)[]
): AllocationResult {
  const perMember = emptyPerMember(memberIds);
  const unassigned: AssignableItem[] = [];
  let allocatedCents = 0;

  for (const item of items) {
    const assignees = item.memberIds.filter((id) =>
      memberIds.some((m) => String(m) === String(id))
    );
    if (assignees.length === 0) {
      unassigned.push(item);
      continue;
    }
    const shares = splitCentsEvenly(item.lineTotalCents, assignees.length);
    assignees.forEach((id, index) => {
      perMember[String(id)] += shares[index];
    });
    allocatedCents += item.lineTotalCents;
  }

  return {perMember, unassigned, allocatedCents};
}

export function sumPerMember(perMember: Record<string, number>): number {
  return Object.values(perMember).reduce((a, b) => a + b, 0);
}
